import { Component } from "@angular/core";
import { ActivatedRoute, ParamMap, Params, Router } from "@angular/router";
import { NgbDateStruct, NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { EMPTY, Observable, of } from "rxjs";
import { switchMap, tap } from "rxjs/operators";
import { CompanyInfo } from "src/app/info/company/company-info";
import { CompanyInfoService } from "src/app/info/company/company-info-service";
import { Page } from "src/app/util/page";
import { InventoryAdjustmentComponent } from "../../adjustment/inventory-adjustment/inventory-adjustment.component";
import { ReferenceInventoryMovement } from "../../inventory-movement/reference-inventory-movement";
import { Reference } from "../reference";
import { ReferenceService } from "../reference.service";
import { SupplierReference } from "../supplier-reference";
import { SwitchSupplierDialogComponent } from "../switch-supplier-reference-dialog/switch-supplier-dialog.component";

@Component({
  selector: "reference-details",
  styleUrls: ["./reference-details.component.css"],
  templateUrl: "./reference-details.component.html",
})
export class ReferencenDetailsComponent {
  public activeTab!: string;
  public companyInfo!: CompanyInfo;
  public reference!: Reference
  public publishDateCA!: NgbDateStruct;
  public publishDateFR!: NgbDateStruct;
  public inventoryMovements!: Page<ReferenceInventoryMovement>;
  public inventoryPage = 0;
  public supplierReference!: SupplierReference;

  public overrideRetailPrice = false;
  public updateError = false;
  public updatingRetailPrice = false;
  public retailPriceOverride!: number;

  constructor(
    activatedRoute: ActivatedRoute,
    companyInfoService: CompanyInfoService,
    private modalService: NgbModal,
    private referenceService: ReferenceService,
    private router: Router
  ) {
    activatedRoute.paramMap.pipe(
      switchMap(paramMap => this.getIsbnFromParams(paramMap)),
      switchMap(isbn => referenceService.findByIsbn(isbn)),
      tap(reference => this.refreshReference(reference)),
      switchMap(_ => this.refreshInventoryMovements()),
    ).subscribe();

    activatedRoute.queryParams.pipe(
      switchMap(params => this.getTabFromParams(params)),
      tap(activeTab => this.activeTab = activeTab)
    ).subscribe();

    companyInfoService.getCompanyInfo().subscribe(companyInfo => this.companyInfo = companyInfo);
  }

  private refreshReference(reference: Reference) {
    this.reference = reference;
    this.computeSupplierReference();
    this.computeDerivedData();
  }

  public cancelRetailPriceOverride() {
    this.retailPriceOverride = this.supplierReference.retailPriceOverride;
    this.overrideRetailPrice = false;
  }

  private computeDate(dateString: string): NgbDateStruct {
    var date = new Date(Date.parse(dateString));

    return {
      year: date.getFullYear(),
      month: date.getMonth() + 1,
      day: date.getDate() + 1
    };
  }

  public computeDerivedData() {
    if (this.reference.publishDateCA != undefined) {
      this.publishDateCA = this.computeDate(this.reference.publishDateCA);
    }
    if (this.reference.publishDateFR != undefined) {
      this.publishDateFR = this.computeDate(this.reference.publishDateFR);
    }
  }

  public computeSupplierReference() {
    for (let supplierReference of this.reference.supplierReferences) {
      if (supplierReference.active) {
        this.supplierReference = supplierReference;

        return;
      }
    }
  }

  public editRetailPriceOverride() {
    this.overrideRetailPrice = true;

    this.retailPriceOverride = (this.supplierReference.retailPriceOverride != null ?
      this.supplierReference.retailPriceOverride :
      this.supplierReference.retailPrice) / 100;
  }

  private getIsbnFromParams(paramMap: ParamMap): Observable<string> {
    let isbn = paramMap.get("isbn") || "";
    if (isbn == "") {
      this.router.navigateByUrl("/404");

      return EMPTY;
    }

    return of(isbn);
  }

  private getTabFromParams(params: Params): Observable<string> {
    var activeTab = params["tab"] || "";
    if (activeTab == "") {
      this.router.navigate([], { queryParams: { tab: "reference" } });

      return EMPTY;
    }

    return of(activeTab);
  }

  public openNewInventoryAdjustmentModal() {
    let modalRef = this.modalService.open(InventoryAdjustmentComponent, {
      backdrop: 'static',
      keyboard: false,
    });

    modalRef.componentInstance.onCreate = () => { this.refreshInventoryMovements().subscribe(); };
    modalRef.componentInstance.reference = this.reference;
  }

  openSwitchSupplierModal() {
    var modalRef = this.modalService.open(SwitchSupplierDialogComponent);

    modalRef.componentInstance.reference = this.reference;
    modalRef.componentInstance.supplier = this.supplierReference
    for (let supplierReference of this.reference.supplierReferences) {
      if (supplierReference.active) {
        modalRef.componentInstance.supplierReference = supplierReference;
      }
    }
    
    modalRef.componentInstance.onSupplierReferenceUpdate = (reference: Reference): void => this.refreshReference(reference);
  }

  private refreshInventoryMovements(): Observable<Page<ReferenceInventoryMovement>> {
    return this.referenceService.findInventoryMovementByIsbn(this.reference.isbn, this.inventoryPage)
      .pipe(tap(inventoryMovements => this.inventoryMovements = inventoryMovements));
  }

  public removeRetailPriceOverride() {
    this.retailPriceOverride = this.supplierReference.retailPrice / 100;

    this.updateRetailPriceOverride();
  }

  public updateRetailPriceOverride() {
    var retailPriceOverride = Math.round(this.retailPriceOverride != 0 ? this.retailPriceOverride * 100 : 0 * 100);

    this.updatingRetailPrice = true;
    this.supplierReference.retailPriceOverride = retailPriceOverride;

    this.referenceService.overrideRetailPrice(this.reference.isbn, retailPriceOverride)

      .subscribe({
        complete: () => {
          this.updatingRetailPrice = false;
          this.overrideRetailPrice = false;
        },
        error: _ => {
          this.updatingRetailPrice = false;
          this.updateError = true;
        },
      });
  }
}